import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressBar } from '@angular/material/progress-bar';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { LoginService } from '@services/login.service';
import { ConsentService } from '@services/consent.service';
import { PreferencesService } from '@services/preferences.service';
import { LoginType } from '@models/registration.models';
import { UtilService } from '@services/util.service';
import { SdkManagerService } from '@services/sdk-manager.service';
import { LogService } from '@services/log.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-sdk-auth',
  template: `
    <section class="d-flex flex-column align-items-center justify-content-center auth-section">
        <div class="loader-container">
            <div>Please wait while we are processing your consent request...</div>
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>
    </section>
  `,
  styles: [`
    .auth-section {
        height: calc(100vh - (64px + 88px )); /* 64px for the header and 88px for the footer */
        padding: 16px;

        .loader-container {
            max-width: 450px;
            width: 100%;
            padding: 0 16px;
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 16px;
            word-wrap: break-word;
            overflow-wrap: break-word;
        }

        @media (max-width: 400px) {
            padding: 12px;

            .loader-container {
                padding: 0 8px;
                gap: 12px;
                font-size: 14px;
            }
        }
    }
  `],
  imports: [MatProgressBar],
})
export class SdkAuthComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loginService = inject(LoginService);
  private consentService = inject(ConsentService);
  private preferenceService = inject(PreferencesService);
  private utilService = inject(UtilService);
  private sdkManagerService = inject(SdkManagerService);
  private logService = inject(LogService);

  async ngOnInit(): Promise<void> {
    this.logService.logPageView('sdk-auth', { queryParams: this.route.snapshot.queryParams });
    // 1. Retrieve the 'consentRequestId' parameter from query params
    this.route.queryParams.subscribe(async (params) => {
      if (params['link']) {
        await this.processOTPAuthFlow(params);
      } else {
        await this.processAuthFlow(params);
      }
    });
  }

  private async processOTPAuthFlow(params: any): Promise<void> {
    // OTP Magic link flow
    const authResult = await this.loginService.completeAuthenticationFlow(params['link']);

    if (!authResult.success) {
      console.error('Authentication failed:', authResult.error);
      // Optionally, navigate to an error page or show error message
      if (authResult.error === 'UNVERIFIED') {
        console.warn('User is not verified, redirecting to register page.');
        this.router.navigate(['/register'], { queryParams: { sdkFlow: true }, queryParamsHandling: 'merge' });
      } else {
        this.router.navigate(['/signin'], { 
          queryParams: { error: authResult.error },
          queryParamsHandling: 'merge' 
        });
      }
      return;
    }

    const { userDetails, redirectInfo: queryParams } = authResult;
    
    if (!userDetails) {
      console.error('No user details received from authentication');
      this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
      return;
    }

    // 3. Navigate based on user verification status and redirect info
    if (userDetails.isVerified) {
      if (queryParams?.consentRequestId) {
        this.router.navigate(['/sdk-handle-consent'], { 
          queryParams
        });
      } else {
        this.router.navigate(['/dashboard']);
      }
    } else {
      this.router.navigate(['/register'], { queryParamsHandling: 'merge' });
    }
  }

  private async processAuthFlow(params: any): Promise<void> {
    try {
      // Step 1: Validate consentRequestId
      const consentRequestId = params['consentRequestId'];
      const apiKey = params['authToken'];
      const parentOrigin = params['referrer'];


      // Test CODE
      // this.router.navigate(['/sdk-handle-consent'], { queryParams: { consentRequestId } });
      // return;

      if (!parentOrigin) {
        console.error('No referrer parameter found in query params. Manually close the consent window.');
      }

      if (!consentRequestId) {
        console.error('No consentRequestId parameter found in query params.');
        this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
        return;
      }

      if (!apiKey) {
        console.error('No authToken parameter found in query params.');
        // Redirect to 403 forbidden page
        return;
      }

      try {
        const decodedApiKey = atob(apiKey);
        const preferences = await lastValueFrom(this.preferenceService.loadPreferences(decodedApiKey));
        if (preferences) {
           this.sdkManagerService.initializeSession(parentOrigin);
           this.sdkManagerService.setWindowMode(preferences.sdkWindowType === 'iframe');
           this.preferenceService.setStandaloneMode(false); // SDK/embedded mode
        } else {
          throw new Error('Website address not found in preferences');
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
         console.error('Invalid authToken provided.');
        // Redirect to 403 forbidden page
        return;
      }
      
      console.log(`Processing consent request ID: ${consentRequestId}`);

      // Step 2: Get consent request details
      const consentRequest = await firstValueFrom(this.consentService.getConsentRequestById(consentRequestId));
      if (!consentRequest || !consentRequest.customerEmail) {
        console.error('No customer email found in consent request');
        this.router.navigate(['/signin'], { queryParamsHandling: 'merge' });
        return;
      }

      const customerEmail = consentRequest.customerEmail;
      const scope = consentRequest?.customConfig?.scope;
      console.log(`Found customer email: ${customerEmail}`);

      // Step 3: Check if user exists
      let userDetails;
      try {
        userDetails = await this.loginService.getUserDetails(customerEmail);
      } catch (userError: any) {
        console.error('Error fetching user details:', userError); 
        // redirect to signin
        this.router.navigate(['/signin'], {queryParamsHandling: 'merge'});
        return;
      }

      // Check if user is verified
      if (!userDetails.isVerified) {
        console.log('User is not verified, redirecting to register');
        this.router.navigate(['/register'], { 
          queryParams: { email: customerEmail, sdkFlow: true },
          queryParamsHandling: 'merge'
        });
        return;
      }

      console.log(`Found verified user details for: ${userDetails.email}`);

      // Step 3.5: Check if user is already signed in with Cognito
      const isCognitoSignedIn = await this.loginService.isCognitoUserSignedIn();
      if (isCognitoSignedIn) {
        const currentUserEmail = await firstValueFrom(this.loginService.loginUserEmail);
        
        if (currentUserEmail === customerEmail) {
          console.log('User is already signed in with matching email, redirecting to sdk-handle-consent');
          this.router.navigate(['/sdk-handle-consent'], { 
            queryParams: { consentRequestId },
            queryParamsHandling: 'merge' 
          });
          return;
        } else {
          console.log('User is signed in but with different email, proceeding with login flow');
          await this.loginService.logout(false);
        }
      }

      // Step 4: Initiate login process
      this.loginService.sendLogin(customerEmail, environment.magicLinkSdk, { consentRequestId }).subscribe({
        next: (loginResult) => {
          console.log('Login process initiated successfully:', loginResult);
          if (loginResult.loginType === LoginType.BYPASS_EMAIL_VERIFICATION) {
            window.location.href = loginResult.metadata || '/dashboard';
          } else {
            this.utilService.openSnackBar('Login initiated successfully! Please check your email for the code.', 'Close', 'success');
            
            // Redirect to signin with email as query param
            this.router.navigate(['/signin'], { 
              queryParams: { email: customerEmail, encryptedData: loginResult.metadata, isSdkFlow: true  },
              queryParamsHandling: 'merge' 
            });
          }
        },
        error: (error) => {
          console.error('Error sending login:', error);
          this.router.navigate(['/signin'], { 
            queryParamsHandling: 'merge' 
          });
        }
      });

    } catch (error) {
      console.error('An error occurred during SDK authentication:', error);
      this.router.navigate(['/signin'], { 
        queryParamsHandling: 'merge' 
      });
    }
  }
}
